import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { SessionService } from '../../_services/session/session.service';
import { AuthenticationService } from '../../_services/authentication/authentication.service';
import ICustomer from '../../_interfaces/ICustomer';
import ISite from '../../_interfaces/ISite';
import IUser from '../../_interfaces/IUser';

// PrimeNG
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';

interface SidebarMenuItem {
  id: string;
  label: string;
  icon: string;
  routerLink?: string;
  children?: SidebarMenuItem[];
  isExpanded?: boolean;
  isActive?: boolean;
  permission?: string;
  moduleIdentifier?: string;
  adminOnly?: boolean;
  superuserOnly?: boolean;
}

@Component({
  standalone: true,
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    MenuModule,
    TooltipModule,
    RippleModule,
    DialogModule,
    ButtonModule,
  ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  @Output() closeSidebar = new EventEmitter<void>();

  // Data
  public user: IUser | null = null;
  public customer: ICustomer | null = null;
  public site: ISite | null = null;
  public sites: ISite[] = [];
  public customers: ICustomer[] = [];
  public availableCustomers: ICustomer[] = [];
  public switchingSiteId: number | null = null;

  // State
  public isSiteSelectorOpen = false;
  public mainMenuItems: SidebarMenuItem[] = [];
  public adminMenuItems: SidebarMenuItem[] = [];
  public isAdminSectionExpanded = false;
  public activeMode: 'professional' | 'patient' = 'professional';
  public availableModes: Array<'professional' | 'patient'> = [];
  public showModeDialog = false;
  public modeToSwitch: 'professional' | 'patient' = 'professional';

  private subscriptions: Subscription[] = [];

  constructor(
    private session: SessionService,
    private authService: AuthenticationService,
    private router: Router,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    // Get initial state from localStorage
    this.user = this.session.getUser();
    this.customer = this.session.getCustomer();
    this.site = this.session.getSite();
    this.customers = this.session.getPermissions();
    this.activeMode = this.session.getActiveMode();
    this.availableModes = this.session.getAvailableModes();
    this.syncAvailableCustomers();

    // Initial menu build
    this.buildMenuItems();

    // Subscribe to session data changes
    const userSub = this.session.getUser$().subscribe((user) => {
      this.user = user;
      this.buildMenuItems();
    });
    this.subscriptions.push(userSub);

    const customerSub = this.session.customer$.subscribe((customer) => {
      this.customer = customer;
      this.buildMenuItems();
    });
    this.subscriptions.push(customerSub);

    const siteSub = this.session.site$.subscribe((site) => {
      this.site = site;
      this.buildMenuItems();
    });
    this.subscriptions.push(siteSub);

    const permsSub = this.session.permissions$.subscribe(() => {
      this.customers = this.session.getPermissions();
      this.syncAvailableCustomers();
      this.buildMenuItems();
    });
    this.subscriptions.push(permsSub);

    const contextSub = this.session.getContext$().subscribe((context) => {
      const activeCustomer = context?.activeCustomer;
      const activeSite = context?.activeSite;

      if (activeCustomer) {
        this.customer = activeCustomer;
      }

      if (activeSite) {
        this.site = activeSite;
      }

      this.activeMode =
        context?.session?.active_mode === 'patient'
          ? 'patient'
          : 'professional';
      this.availableModes = this.normalizeModes(context?.availableModes);

      this.syncAvailableCustomers();
      this.buildMenuItems();
    });
    this.subscriptions.push(contextSub);

    // Rebuild menu on language change
    const langSub = this.translateService.onLangChange.subscribe(() => {
      this.buildMenuItems();
    });
    this.subscriptions.push(langSub);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  // =========================================================================
  // MENU BUILDING
  // =========================================================================

  private buildMenuItems() {
    if (this.isPatientMode) {
      this.mainMenuItems = [
        {
          id: 'patient-home',
          label: this.translateService.instant('PATIENT_SPACE.NAV.HOME'),
          icon: 'fas fa-house',
          routerLink: '/patient/home',
        },
        {
          id: 'patient-discover',
          label: this.translateService.instant('PATIENT_SPACE.NAV.DISCOVER'),
          icon: 'fas fa-compass',
          routerLink: '/patient/discover',
        },
        {
          id: 'patient-appointments',
          label: this.translateService.instant(
            'PATIENT_SPACE.NAV.APPOINTMENTS',
          ),
          icon: 'fas fa-calendar-alt',
          routerLink: '/patient/appointments',
        },
        {
          id: 'patient-clinics',
          label: this.translateService.instant('PATIENT_SPACE.NAV.CLINICS'),
          icon: 'fas fa-hospital',
          routerLink: '/patient/clinics',
        },
        {
          id: 'patient-purchases',
          label: this.translateService.instant('PATIENT_SPACE.NAV.PURCHASES'),
          icon: 'fas fa-shopping-bag',
          routerLink: '/patient/purchases',
        },
        {
          id: 'patient-profile',
          label: this.translateService.instant('PATIENT_SPACE.NAV.PROFILE'),
          icon: 'fas fa-user',
          routerLink: '/patient/profile',
        },
      ];
      this.adminMenuItems = [];
      this.isAdminSectionExpanded = false;
      return;
    }

    if (this.session.isSuperUser() && !this.session.isActingContext()) {
      this.mainMenuItems = [];
      this.adminMenuItems = this.buildSuperuserAdminItems();
      this.isAdminSectionExpanded = true;
      return;
    }

    const adminCustomer =
      this.customer || this.resolveAdminCustomerForWorkspace();

    if (!this.customer && adminCustomer) {
      this.customer = adminCustomer;
    }

    if (!this.customer || !this.site) {
      this.mainMenuItems = [];

      if (adminCustomer && this.canShowAdminSection()) {
        this.adminMenuItems = [
          {
            id: 'admin-workspace',
            label: this.translateService.instant('ADMIN.WORKSPACE.TITLE'),
            icon: 'fas fa-cogs',
            routerLink: `/administration/${adminCustomer.identifier || adminCustomer.id}/users`,
          },
        ];

        if (this.isAdmin()) {
          this.adminMenuItems.unshift(...this.buildSuperuserAdminItems());
        }
      } else {
        this.adminMenuItems = [];
      }
      return;
    }

    const customerRouteKey =
      `${this.customer.identifier || this.customer.id || ''}`.trim();
    const siteRouteKey = `${this.site.identifier || this.site.id || ''}`.trim();

    if (!customerRouteKey || !siteRouteKey) {
      this.mainMenuItems = [];
      this.adminMenuItems = [];
      return;
    }

    const baseRoute = `${customerRouteKey}/${siteRouteKey}`;

    // Main navigation items
    this.mainMenuItems = [
      {
        id: 'dashboard',
        label: this.translateService.instant('MENU.DASHBOARD'),
        icon: 'fas fa-th-large',
        routerLink: `/${baseRoute}/dashboard`,
        permission: 'DASHBOARD.READ',
        moduleIdentifier: 'dashboard',
      },
      {
        id: 'appointments',
        label: this.translateService.instant('MENU.APPOINTMENTS'),
        icon: 'fas fa-calendar-alt',
        routerLink: `/${baseRoute}/appointments`,
        moduleIdentifier: 'appointments',
      },
      {
        id: 'patients',
        label: this.translateService.instant('MENU.PATIENTS'),
        icon: 'fas fa-users',
        routerLink: `/${baseRoute}/patients`,
        moduleIdentifier: 'patients',
      },
      {
        id: 'sales',
        label: this.translateService.instant('MENU.SALES'),
        icon: 'fas fa-cash-register',
        routerLink: `/${baseRoute}/sales`,
        permission: 'SALES.READ',
        moduleIdentifier: 'sales',
      },
      {
        id: 'statistics',
        label: this.translateService.instant('MENU.STATS'),
        icon: 'fas fa-chart-pie',
        routerLink: `/${baseRoute}/statistics`,
        permission: 'STATISTICS.READ',
        moduleIdentifier: 'statistics',
      },
      {
        id: 'tasks',
        label: this.translateService.instant('MENU.TASKS'),
        icon: 'fas fa-tasks',
        routerLink: `/${baseRoute}/tasks`,
        moduleIdentifier: 'tasks',
      },
      {
        id: 'reminders',
        label: this.translateService.instant('MENU.REMINDERS'),
        icon: 'fas fa-bell',
        routerLink: `/${baseRoute}/reminders`,
        moduleIdentifier: 'reminders',
      },
      {
        id: 'ai-assistant',
        label: this.translateService.instant('MENU.AI_ASSISTANT'),
        icon: 'fas fa-robot',
        routerLink: `/${baseRoute}/ai-assistant`,
        permission: 'AI_ASSISTANT.READ',
        moduleIdentifier: 'ai',
      },
      {
        id: 'incidents',
        label: this.translateService.instant('MENU.INCIDENTS'),
        icon: 'fas fa-triangle-exclamation',
        routerLink: `/${baseRoute}/incidents`,
        permission: 'INCIDENTS.READ',
        moduleIdentifier: 'incidents',
      },
      {
        id: 'online-booking',
        label: this.translateService.instant('MENU.ONLINE_BOOKING'),
        icon: 'fas fa-qrcode',
        routerLink: `/${baseRoute}/online-booking`,
        moduleIdentifier: 'online_booking',
      },
      {
        id: 'support',
        label: this.translateService.instant('SUPPORT.MY_TICKETS'),
        icon: 'fas fa-headset',
        routerLink: '/support',
      },
    ].filter((item) => this.canShowMenuItem(item));

    // Admin section items
    if (this.canShowAdminSection()) {
      this.adminMenuItems = [
        {
          id: 'admin-workspace',
          label: this.translateService.instant('ADMIN.WORKSPACE.TITLE'),
          icon: 'fas fa-cogs',
          routerLink: `/administration/${this.customer.identifier || this.customer.id}/users`,
        },
      ];

      // Admin-only items (superuser access)
      if (this.isAdmin()) {
        this.adminMenuItems.unshift(...this.buildSuperuserAdminItems());
      }
    }
  }

  private buildSuperuserAdminItems(): SidebarMenuItem[] {
    return [
      {
        id: 'admin-customers',
        label: this.translateService.instant('ADMIN.CUSTOMERS.TITLE'),
        icon: 'fas fa-building',
        routerLink: '/administration/customers',
        superuserOnly: true,
      },
      {
        id: 'admin-contact-leads',
        label: this.translateService.instant('ADMIN.CONTACT_LEADS.NAV'),
        icon: 'fas fa-inbox',
        routerLink: '/administration/contact-leads',
        superuserOnly: true,
      },
      {
        id: 'admin-support',
        label: this.translateService.instant('SUPPORT.ADMIN_TITLE'),
        icon: 'fas fa-headset',
        routerLink: '/administration/support',
        superuserOnly: true,
      },
      {
        id: 'admin-business-categories',
        label: this.translateService.instant('ADMIN.BUSINESS_CATEGORIES.NAV'),
        icon: 'fas fa-tags',
        routerLink: '/administration/customers/categories',
        superuserOnly: true,
      },
      {
        id: 'admin-global-pricing',
        label: this.translateService.instant(
          'ADMIN.CUSTOMERS.PRICING_GLOBAL.NAV',
        ),
        icon: 'fas fa-euro-sign',
        routerLink: '/administration/customers/pricing',
        superuserOnly: true,
      },
      {
        id: 'admin-global-email',
        label: this.translateService.instant('ADMIN.EMAIL_ACCOUNTS.GLOBAL_NAV'),
        icon: 'fas fa-envelope',
        routerLink: '/administration/customers/email-account',
        superuserOnly: true,
      },
    ];
  }

  private resolveAdminCustomerForWorkspace(): ICustomer | null {
    const effectiveCustomerId = this.session.getEffectiveCustomerId();
    if (effectiveCustomerId) {
      const effectiveCustomer = (this.customers || []).find(
        (customer) => +customer.id === +effectiveCustomerId,
      );
      if (effectiveCustomer) {
        return effectiveCustomer;
      }
    }

    const customerAdmin = (this.customers || []).find((customer) =>
      this.session.isCustomerAdmin(customer),
    );
    if (customerAdmin) {
      return customerAdmin;
    }

    return (this.customers || [])[0] || null;
  }

  private canShowMenuItem(item: SidebarMenuItem): boolean {
    const hasPermission = !item.permission
      ? true
      : this.session.hasPermission(this.customer, this.site, item.permission);
    if (!hasPermission) return false;

    if (!item.moduleIdentifier) return true;
    return this.isCommercialModuleEnabled(item.moduleIdentifier);
  }

  private canShowAdminSection(): boolean {
    return this.isAdmin() || this.isAdminOnSomeCustomer();
  }

  get isPatientMode(): boolean {
    return this.activeMode === 'patient';
  }

  get canSwitchMode(): boolean {
    return (this.availableModes || []).length > 1;
  }

  private normalizeModes(values: any): Array<'professional' | 'patient'> {
    const modes = Array.isArray(values) ? values : [];
    const normalized = modes.filter(
      (mode: string) => mode === 'professional' || mode === 'patient',
    );
    return normalized.length ? normalized : this.session.getAvailableModes();
  }

  private normalizeCommercialModuleIdentifier(identifier: string): string {
    const raw = `${identifier || ''}`.trim().toLowerCase();
    if (raw === 'statistics') {
      return 'analytics';
    }
    return raw;
  }

  private isCommercialModuleEnabled(identifier: string): boolean {
    const normalized = this.normalizeCommercialModuleIdentifier(identifier);
    if (normalized === 'appointments' || normalized === 'patients') {
      return true;
    }

    const modules = this.customer?.commercialModules;
    if (!modules) return !['reminders', 'incidents'].includes(normalized);
    const value = (modules as any)?.[normalized];
    return value !== false;
  }

  private isAdmin(): boolean {
    return this.session.isSuperUser();
  }

  private isAdminOnSomeCustomer(): boolean {
    return (this.customers || []).some((customer) =>
      this.session.isCustomerAdmin(customer),
    );
  }

  // =========================================================================
  // SITE SELECTOR
  // =========================================================================

  toggleSiteSelector() {
    this.isSiteSelectorOpen = !this.isSiteSelectorOpen;
  }

  selectSite(customer: ICustomer, site: ISite) {
    if (!site?.id) return;
    this.switchingSiteId = +site.id;
    this.authService.switchClinic(+site.id).subscribe({
      next: (context: any) => {
        this.switchingSiteId = null;
        this.isSiteSelectorOpen = false;
        this.authService.applyContext(context);
        this.router.navigate(['/entry']);
        if (window.innerWidth < 992) {
          this.closeSidebar.emit();
        }
      },
      error: () => {
        this.switchingSiteId = null;
      },
    });
  }

  getSitesForCustomer(customer: ICustomer): ISite[] {
    return customer.sites || [];
  }

  private syncAvailableCustomers() {
    const effectiveCustomerId = this.session.getEffectiveCustomerId();
    if (!effectiveCustomerId) {
      this.availableCustomers = this.customers || [];
      return;
    }

    const scoped = (this.customers || []).filter(
      (customer) => +customer.id === +effectiveCustomerId,
    );
    this.availableCustomers = scoped.length ? scoped : this.customers || [];
  }

  // =========================================================================
  // ADMIN SECTION
  // =========================================================================

  toggleAdminSection() {
    this.isAdminSectionExpanded = !this.isAdminSectionExpanded;
  }

  // =========================================================================
  // NAVIGATION
  // =========================================================================

  navigateTo(item: SidebarMenuItem) {
    if (item.routerLink) {
      this.closeSidebar.emit();
    }
  }

  isItemActive(item: SidebarMenuItem): boolean {
    if (!item.routerLink) return false;
    return this.router.url.includes(item.routerLink);
  }

  // =========================================================================
  // USER ACTIONS
  // =========================================================================

  logout() {
    this.authService.logout();
  }

  goToProfile() {
    this.router.navigate(['/profile']);
    this.closeSidebar.emit();
  }

  switchMode() {
    if (!this.canSwitchMode) return;
    const fallbackMode: 'professional' | 'patient' =
      this.activeMode === 'patient' ? 'professional' : 'patient';
    const available = this.availableModes.includes(fallbackMode)
      ? fallbackMode
      : this.availableModes.find((mode) => mode !== this.activeMode) ||
        fallbackMode;
    this.modeToSwitch = available;
    this.showModeDialog = true;
  }

  confirmSwitchMode() {
    if (!this.modeToSwitch || this.modeToSwitch === this.activeMode) {
      this.showModeDialog = false;
      return;
    }
    this.authService.switchMode(this.modeToSwitch).subscribe({
      next: (context: any) => {
        this.authService.applyContext(context);
        this.showModeDialog = false;
        this.closeSidebar.emit();
        this.router.navigate(['/entry']);
      },
      error: () => {
        this.showModeDialog = false;
      },
    });
  }

  cancelSwitchMode() {
    this.showModeDialog = false;
  }
}
